import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Screen } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/Button';
import { LinkButton } from '@/components/ui/LinkButton';
import { Banner, Card, CardTitle, ProgressBar, Spinner } from '@/components/ui/Card';
import { Select } from '@/components/ui/Form';
import { useAppData } from '@/services/data/dataContext';
import { useAsync } from '@/hooks/useAsync';
import { computeSceneStatistics, computeShowStatistics } from '@/services/progress/progressService';
import { pluralize } from '@/lib/text';
import { roleConflicts } from '@/lib/roles';
import type { CharacterGender } from '@/types/domain';

interface CarryOver {
  carriedOver: number;
  total: number;
  orphaned: number;
}

export function ShowDetailScreen() {
  const { showId = '' } = useParams();
  const { data } = useAppData();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [pendingRoles, setPendingRoles] = useState<string[] | null>(null);
  const [pendingGenders, setPendingGenders] = useState<Record<string, CharacterGender>>({});
  const gendersRef = useRef<Record<string, CharacterGender>>({});

  const carriedOver = (location.state as { carriedOver?: CarryOver } | null)?.carriedOver ?? null;

  const detail = useAsync(async () => {
    const [show, script, progress] = await Promise.all([
      data.getShow(showId),
      data.getScript(showId),
      data.getProgress(showId),
    ]);
    return { show, script, progress };
  }, [data, showId]);

  if (detail.loading) return <Spinner label="Loading show" />;

  const show = detail.data?.show;
  const script = detail.data?.script;
  const progress = detail.data?.progress;

  if (!show || !script || !progress) {
    return (
      <>
        <TopBar title="Show" showBack />
        <Screen>
          <Banner tone="danger">This show could not be loaded.</Banner>
        </Screen>
      </>
    );
  }

  // Held locally so ticking a role repaints instantly; the save runs behind it.
  const roles = pendingRoles ?? show.myRoles;
  const overall = computeShowStatistics(script, progress, roles);
  const scenes = computeSceneStatistics(script, progress, roles);
  const conflicts = roleConflicts(script, roles);

  const toggleRole = (characterId: string) => {
    const next = roles.includes(characterId)
      ? roles.filter((id) => id !== characterId)
      : [...roles, characterId];
    setPendingRoles(next);
    void data.setMyRoles(showId, next);
  };

  const remove = async () => {
    setBusy(true);
    await data.deleteShow(showId);
    navigate('/shows', { replace: true });
  };

  const changeGender = (characterId: string, gender: CharacterGender) => {
    // Accumulated, because each save writes the whole script: building from the
    // render's copy would drop a change made a moment earlier.
    const next = { ...gendersRef.current, [characterId]: gender };
    gendersRef.current = next;
    setPendingGenders(next);

    void data.saveScript({
      ...script,
      characters: script.characters.map((c) => ({ ...c, gender: next[c.id] ?? c.gender })),
      updatedAt: new Date().toISOString(),
    });
  };

  return (
    <>
      <TopBar title={show.title} subtitle={`${script.scenes.length} scenes`} showBack />
      <Screen>
        {carriedOver ? (
          <Banner tone={carriedOver.orphaned > 0 ? 'warn' : 'info'}>
            Script updated. {carriedOver.carriedOver} of {carriedOver.total} tracked{' '}
            {pluralize(carriedOver.total, 'line')} kept their progress
            {carriedOver.orphaned > 0
              ? `; ${carriedOver.orphaned} ${pluralize(carriedOver.orphaned, 'line')} no longer ${
                  carriedOver.orphaned === 1 ? 'matches' : 'match'
                } the script and ${carriedOver.orphaned === 1 ? 'was' : 'were'} left behind.`
              : '.'}
          </Banner>
        ) : null}

        {/* Same shape whether or not roles are picked, so ticking one does not shift the page. */}
        <Card className="flex flex-col gap-3">
          <ProgressBar percent={overall.progressPercent} label="Memorization progress" />
          <p className="text-ink-500 dark:text-ink-400 text-sm">
            {roles.length > 0
              ? `Playing ${roles.length} ${pluralize(roles.length, 'part')} · ${overall.memorized} of ${overall.totalLines} lines memorized · ${Math.round(overall.accuracy * 100)}% accuracy`
              : 'Tick the parts you play in the cast below to unlock rehearsal.'}
          </p>
          {roles.length > 0 ? (
            <LinkButton to={`/practice/${showId}`} size="lg" block>
              Rehearse
            </LinkButton>
          ) : (
            <Button size="lg" block disabled>
              Rehearse
            </Button>
          )}
        </Card>

        <Card className="flex flex-col gap-3">
          <CardTitle>Cast</CardTitle>
          <p className="text-ink-500 dark:text-ink-400 text-sm">
            Tick the parts you play — you can double across the show, but not two in one scene.
            Voice type keeps the other characters apart when they are read aloud.
          </p>

          {conflicts.map((conflict) => (
            <Banner key={conflict.sceneId} tone="danger">
              {conflict.characterIds
                .map((id) => script.characters.find((c) => c.id === id)?.name ?? id)
                .join(' and ')}{' '}
              both appear in {conflict.sceneName} — one actor cannot play them in the same scene.
            </Banner>
          ))}

          <ul className="flex flex-col gap-1">
            {script.characters.map((character) => (
              <li key={character.id} className="flex items-center gap-3">
                <label className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    className="size-5 shrink-0"
                    checked={roles.includes(character.id)}
                    onChange={() => toggleRole(character.id)}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {character.name}
                    <span className="text-ink-500 dark:text-ink-400">
                      {' '}
                      · {character.lineCount}
                    </span>
                  </span>
                </label>
                {/* Sized wrapper: the select's own class list is w-full. */}
                <div className="w-28 shrink-0">
                  <Select
                    aria-label={`Voice for ${character.name}`}
                    className="px-2 py-2 text-sm"
                    value={pendingGenders[character.id] ?? character.gender ?? 'neutral'}
                    onChange={(event) =>
                      changeGender(character.id, event.target.value as CharacterGender)
                    }
                  >
                    <option value="neutral">Any voice</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                  </Select>
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="flex flex-col gap-3">
          <CardTitle>Scenes</CardTitle>
          <ul className="flex flex-col gap-3">
            {scenes.map((scene) => (
              <li key={scene.sceneId} className="flex flex-col gap-1.5">
                <div className="flex justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{scene.name}</span>
                  <span className="text-ink-500 dark:text-ink-400 tabular-nums">
                    {scene.totalLines} {pluralize(scene.totalLines, 'line')}
                  </span>
                </div>
                <ProgressBar percent={scene.progressPercent} label={`${scene.name} progress`} />
              </li>
            ))}
          </ul>
        </Card>

        <Card className="flex flex-col gap-3">
          <CardTitle>Script</CardTitle>
          <p className="text-ink-500 dark:text-ink-400 text-sm">
            Fixed a typo or got a new draft? Re-import it. Lines whose wording is unchanged keep
            their progress.
          </p>
          <LinkButton to={`/shows/new?showId=${showId}`} variant="secondary" block>
            Update script
          </LinkButton>
        </Card>

        {confirmingDelete ? (          <Card className="flex flex-col gap-3">
            <p className="text-sm">
              Delete <strong>{show.title}</strong> and all of its progress? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="danger" block disabled={busy} onClick={() => void remove()}>
                Delete permanently
              </Button>
              <Button variant="secondary" block onClick={() => setConfirmingDelete(false)}>
                Cancel
              </Button>
            </div>
          </Card>
        ) : (
          <Button variant="ghost" onClick={() => setConfirmingDelete(true)}>
            Delete show
          </Button>
        )}
      </Screen>
    </>
  );
}
